import { isEqual } from 'lodash';
import { Timestamp } from 'firebase/firestore';

/**
 * Cache for the processTimestamps function
 */
const cache = {
  lastObj: null as any,
  lastProcessedObj: null as any
};

/**
 * Processes Firebase Timestamp objects in an object tree and converts them to JavaScript Date objects.
 * Uses caching to avoid reprocessing the same object.
 *
 * @param obj - The object to process
 * @returns The processed object with Timestamps converted to Dates
 */
export function processTimestamps<T>(obj: T): T {
  if (isEqual(obj, cache.lastObj)) {
    return cache.lastProcessedObj;
  }

  const processedObj = processObject(obj);
  cache.lastObj = obj;
  cache.lastProcessedObj = processedObj;
  return processedObj;
}

/**
 * Helper function that recursively processes an object tree to convert Firebase Timestamps to Date objects
 */
function processObject<T>(obj: T): any {
  if (obj instanceof Timestamp) {
    return (obj as Timestamp).toDate();
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => processObject(item));
  }

  if (typeof obj === 'object' && obj !== null) {
    const newObj: Record<string, any> = { ...obj };
    for (let key in newObj) {
      if (Object.prototype.hasOwnProperty.call(newObj, key)) {
        newObj[key] = processObject(newObj[key]);
      }
    }
    return newObj;
  }

  return obj;
}

/**
 * Safely converts a value that should be a Firestore Timestamp to a Date.
 * Handles real Timestamp instances, plain {seconds, nanoseconds} objects,
 * and any other unexpected shapes by returning null.
 */
export function toSafeDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (
    typeof value === 'object' &&
    'seconds' in (value as any) &&
    'nanoseconds' in (value as any)
  ) {
    try {
      return new Timestamp(
        (value as any).seconds,
        (value as any).nanoseconds,
      ).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

export function dayliteEventTitleExtractedDate(eventDayliteName: string): string {
  if (!eventDayliteName) {
    return '';
  }

  if (eventDayliteName.length < 8) {
    return '';
  }

  const dateString = eventDayliteName.substring(0, 8);
  const dateRegex = /^\d{2}-\d{2}-\d{2}$/;

  return dateRegex.test(dateString) ? dateString : '';
}

export function dayliteEventTitleExtractedDateYYYYMMDD(eventDayliteName: string): string {
  const extractedDate = dayliteEventTitleExtractedDate(eventDayliteName);

  if (!extractedDate) {
    return '';
  }

  const month = extractedDate.substring(0, 2);
  const day = extractedDate.substring(3, 5);
  const year = `20${extractedDate.substring(6, 8)}`; // Assuming the year is in the 2000s

  return `${year}-${month}-${day}`;
}

/**
 * Converts a date string (YYYY-MM-DD or ISO format) to a Date object using local timezone
 * This avoids timezone shift issues when working with date-only strings
 * 
 * @param dateString - Date string in YYYY-MM-DD or full ISO format
 * @returns Date object in local timezone
 */
export function dateStringToLocalDate(dateString: string): Date {
  // Extract YYYY-MM-DD portion if full ISO string
  const dateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  
  // Parse components manually to avoid UTC interpretation
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  // Create date in local timezone (month is 0-indexed)
  return new Date(year, month - 1, day);
}

/**
 * Converts a Date object to YYYY-MM-DD format using local timezone
 * This avoids timezone shift issues when converting dates to strings
 * 
 * @param date - Date object
 * @returns Date string in YYYY-MM-DD format
 */
export function dateToLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}
